import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Alert,
    TextField,
    Typography,
} from '@mui/material';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import CommonHeader from '../../base/views/CommonHeader';
import ApiService from '../../base/api/ApiService';
import { Trip } from '../../components/models/Trip';
import { TripStudent } from '../../components/models/TripStudent';

const KINSHIP_OPTIONS = ['Мать', 'Отец', 'Бабушка', 'Дедушка', 'Тетя', 'Дядя'];

const TripDetailPage: React.FC = () => {
    const { tripId } = useParams<{ tripId: string }>();
    const [searchParams] = useSearchParams();
    const studentId = searchParams.get('studentId') ?? undefined;

    const [trip, setTrip] = useState<Trip | null>(null);
    const [tripStudent, setTripStudent] = useState<TripStudent | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const [accompaniedByAParent, setAccompaniedByAParent] = useState(true);
    const [nameOfAccompanying, setNameOfAccompanying] = useState('');
    const [surnameOfAccompanying, setSurnameOfAccompanying] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [kinship, setKinship] = useState('');
    const [isweGetThereOnOurOwn, setIsweGetThereOnOurOwn] = useState(false);

    useEffect(() => {
        if (!tripId) return;
        ApiService.getTripClientDetail(tripId, studentId)
            .then((data) => {
                const t = new Trip();
                Object.assign(t, data);
                setTrip(t);

                if (data.tripStudent) {
                    const ts = new TripStudent();
                    Object.assign(ts, data.tripStudent);
                    setTripStudent(ts);

                    const hasCompanionInfo = !!(ts.nameOfAccompanying || ts.surnameOfAccompanying);
                    const hasClient = !!(ts.clientId);
                    // Checked by default when client is linked and no other companion was saved
                    setAccompaniedByAParent(hasCompanionInfo ? false : hasClient);

                    setNameOfAccompanying(ts.nameOfAccompanying ?? '');
                    setSurnameOfAccompanying(ts.surnameOfAccompanying ?? '');
                    setPhoneNumber(ts.phoneNumber ?? '');
                    setKinship(ts.kinship?.[0] ?? '');
                    setIsweGetThereOnOurOwn(ts.isweGetThereOnOurOwn ?? false);
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [tripId, studentId]);

    const formatDate = (dateStr: string | null | undefined): string => {
        if (!dateStr) return '—';
        try {
            return format(new Date(dateStr), 'd MMMM yyyy', { locale: ru });
        } catch {
            return dateStr;
        }
    };

    const formatDateTime = (dateStr: string | null | undefined): string => {
        if (!dateStr) return '—';
        try {
            return format(new Date(dateStr), 'd MMMM yyyy, HH:mm', { locale: ru });
        } catch {
            return dateStr;
        }
    };

    const formatDateRange = (): string => {
        const start = formatDate(trip?.dateStart);
        const end = formatDate(trip?.dateEnd);
        if (end === '—' || start === end) return start;
        return `${start} — ${end}`;
    };

    const handleSave = async () => {
        if (!tripId) return;
        setSaving(true);
        try {
            await ApiService.saveTripCompanion(
                tripId,
                {
                    accompaniedByAParent,
                    nameOfAccompanying: accompaniedByAParent ? undefined : nameOfAccompanying,
                    surnameOfAccompanying: accompaniedByAParent ? undefined : surnameOfAccompanying,
                    phoneNumber: accompaniedByAParent ? undefined : phoneNumber,
                    kinship: accompaniedByAParent || !kinship ? undefined : [kinship],
                    isweGetThereOnOurOwn,
                },
                studentId
            );
            setSnackbar({ open: true, message: 'Сохранено', severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: 'Ошибка при сохранении', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
    }

    if (!trip) {
        return <Typography sx={{ p: 2 }}>Поездка не найдена</Typography>;
    }

    return (
        <div>
            <CommonHeader title={trip.name ?? 'Поездка'} showBackButton={true} />

            <Box sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <DirectionsBusIcon color="primary" />
                    <Typography variant="h6">{trip.name}</Typography>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" flexDirection="column" gap={1.5}>
                    <Box display="flex" gap={1} alignItems="flex-start">
                        <AccessTimeIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                        <Box>
                            <Typography variant="caption" color="textSecondary">Даты мероприятия</Typography>
                            <Typography variant="body2">{formatDateRange()}</Typography>
                        </Box>
                    </Box>

                    {trip.departureDateAndTime && (
                        <Box display="flex" gap={1} alignItems="flex-start">
                            <AccessTimeIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                            <Box>
                                <Typography variant="caption" color="textSecondary">Дата и время отправления</Typography>
                                <Typography variant="body2">{formatDateTime(trip.departureDateAndTime)}</Typography>
                            </Box>
                        </Box>
                    )}

                    {trip.nameOfCompetition && (
                        <Box display="flex" gap={1} alignItems="flex-start">
                            <EmojiEventsIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                            <Box>
                                <Typography variant="caption" color="textSecondary">Название конкурса</Typography>
                                <Typography variant="body2">{trip.nameOfCompetition}</Typography>
                            </Box>
                        </Box>
                    )}

                    {trip.cityOfCompetition && (
                        <Box display="flex" gap={1} alignItems="flex-start">
                            <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                            <Box>
                                <Typography variant="caption" color="textSecondary">Город проведения</Typography>
                                <Typography variant="body2">{trip.cityOfCompetition}</Typography>
                            </Box>
                        </Box>
                    )}

                    {trip.gatheringPlace && (
                        <Box display="flex" gap={1} alignItems="flex-start">
                            <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                            <Box>
                                <Typography variant="caption" color="textSecondary">Место сбора</Typography>
                                <Typography variant="body2">{trip.gatheringPlace}</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                {tripStudent && (
                    <>
                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <PeopleIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold">Сопровождение</Typography>
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={accompaniedByAParent}
                                    onChange={(e) => setAccompaniedByAParent(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Я сопровождаю ученика"
                        />

                        {!accompaniedByAParent && (
                            <Box display="flex" flexDirection="column" gap={1.5} mt={1.5}>
                                <TextField
                                    label="Имя сопровождающего"
                                    value={nameOfAccompanying}
                                    onChange={(e) => setNameOfAccompanying(e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                                <TextField
                                    label="Фамилия сопровождающего"
                                    value={surnameOfAccompanying}
                                    onChange={(e) => setSurnameOfAccompanying(e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                                <TextField
                                    label="Телефон"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 15 }}
                                />
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Кем приходится</InputLabel>
                                    <Select
                                        value={kinship}
                                        label="Кем приходится"
                                        onChange={(e) => setKinship(e.target.value)}
                                    >
                                        <MenuItem value=""><em>Не выбрано</em></MenuItem>
                                        {KINSHIP_OPTIONS.map((k) => (
                                            <MenuItem key={k} value={k}>{k}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        <FormControlLabel
                            sx={{ mt: 1.5, display: 'block' }}
                            control={
                                <Checkbox
                                    checked={isweGetThereOnOurOwn}
                                    onChange={(e) => setIsweGetThereOnOurOwn(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Добираемся самостоятельно"
                        />

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving}
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            {saving ? <CircularProgress size={20} color="inherit" /> : 'Сохранить'}
                        </Button>
                    </>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default TripDetailPage;
